/** Base backend route for the CSV Merge remote API. */
const REMOTE_API = 'remoteApi/csv/merge';

/**
 * Recursively flattens a (possibly nested) plain object into a `FormData`
 * instance, using `parentKey[childKey]` bracket notation for nested keys.
 * `File` values are appended as-is (not recursed into).
 * @param obj - Source object to convert.
 * @param formData - Accumulator `FormData` instance (created on first call).
 * @param parentKey - Key prefix used when recursing into nested objects.
 * @returns The populated `FormData` instance.
 */
function objectToFormData (obj: any, formData: FormData = new FormData(), parentKey: string = ''): FormData {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            const fullKey = parentKey ? `${parentKey}[${key}]` : key;

            if (value && typeof value === 'object' && !(value instanceof File)) {
                objectToFormData(value, formData, fullKey);
            } else {
                formData.append(fullKey, value);
            }
        }
    }

    return formData;
}

/**
 * Parsed pieces of a CSV Merge source URL's `upstream`/`market`/`autopricer`
 * query parameters.
 */
interface IDataUpstream {
    /** Everything before the `?` in the source URL. */
    BaseUrl: string;
    /** Raw `upstream` query value, if present. */
    Upstream: string | undefined;
    /** Raw `market` query value, if present. */
    Market: string | undefined;
    /** `true` when the `autopricer` query value is `1`. */
    AutoPricer: boolean;
    /** Remaining query string parameters after removing `upstream`/`market`/`autopricer`. */
    Anothers: string;
}

/**
 * Extracts `upstream`, `market`, and `autopricer` query parameters from a
 * CSV Merge source URL and returns the remaining query string separately.
 * @param url - Raw source URL (used to derive `BaseUrl` and parse parameters).
 * @param formattedUrl - Optional already-formatted URL to parse `market`/`autopricer` from instead of `url`.
 * @returns The parsed URL pieces.
 */
function getDataUpstream (url: string, formattedUrl?: string): IDataUpstream {
    let urlData = url.split('?');
    let parameters = urlData[1] || '';
    let upstreams = url.match(/(?:upstream=)(.[\w.*,]*)/)?.[1];
    let market = (formattedUrl || url).match(/(?:market=)(\w+)/)?.[1];
    let autopricer = (formattedUrl || url).match(/(?:autopricer=)(\w+)/)?.[1];
    if (parameters) {
        if (upstreams) {
            parameters = parameters.replace(`upstream=${upstreams}`, '');
        }
        if (market) {
            parameters = parameters.replace(`&market=${market}`, '').replace(`market=${market}`, '');
        }
        if (autopricer) {
            parameters = parameters.replace(`&autopricer=${autopricer}`, '').replace(`autopricer=${autopricer}`, '');
        }
        if (parameters[0] === '&') {
            parameters = parameters.slice(1, parameters.length);
        }
    }
    return {
        BaseUrl: urlData[0],
        Upstream: upstreams,
        Market: market,
        // Legacy '1'/'0' wire format; loose comparison coerces `true` to the number 1 so it
        // only matches the string '1', mirroring the original `== true` idiom.
        AutoPricer: autopricer === '1',
        Anothers: parameters || ''
    };
}

/**
 * Convert string like 'abc,xyx' => ['abc','xyz']
 * @param value - Comma-separated string, an already-built array, or a nullish value.
 * @returns An array of trimmed, non-empty strings; the input unchanged if it isn't a string; or `[]` for nullish input.
 */
function convertStringToArray (value: string | string[] | null | undefined): string[] {
    if (!value) return [];
    if (typeof value === 'string') return value.split(',').filter((x: string) => x).map((str: string) => str.trim());
    return value;
}

export {
    REMOTE_API,
    objectToFormData,
    getDataUpstream,
    convertStringToArray
};
